import React, { useMemo } from 'react';
import { Video } from '@fiverr-private/media';
import { Center } from '@fiverr-private/layout_components';
import { AudioL1Icon, ErrorCircleIcon, FileL1Icon, ImageL1Icon, PdfL1Icon, VideoL1Icon } from '@fiverr-private/visuals';
import { Box } from '@fiverr-private/theme';
import { getContext } from '@fiverr-private/fiverr_context';
import { type AssetProps } from './types';
import { getFileType, getSize } from './utils';

const Asset = ({
  showPreview,
  hasError,
  variant,
  width: widthProp = '100%',
  height: heightProp = '100%',
  url,
  name,
  children,
  theme: themeProp,
}: AssetProps) => {
  const { isImage, isAudio, isDocument, isPdf, isVideo, isOther } = useMemo(() => getFileType(name), [name]);
  const { width, height } = getSize(variant, widthProp, heightProp);
  const { isTouch } = getContext();
  const isDark = themeProp === 'dark';

  const backgroundColor = isDark ? 'black_50' : 'grey_200';
  const errorColor = isDark ? 'red_1200' : 'red_100';

  return (
    <Center
      overflow="hidden"
      key={name}
      borderRadius="lg"
      backgroundColor={hasError ? errorColor : backgroundColor}
      flexShrink={0}
      width={width}
      height={height}
      display="flex"
      direction="column"
      color={isDark ? 'grey_700' : 'grey_1200'}
    >
      {hasError ? (
        <>
          <Center gap="1">
            <ErrorCircleIcon color={isDark ? 'red_600' : 'red_900'} size={variant === 'list' ? 'sm' : 'lg'} />
            {isTouch && children}
          </Center>
        </>
      ) : (
        <>
          {isImage && showPreview && url ? (
            <Box
              as="img"
              alt={name}
              src={url}
              objectFit="cover"
              style={{
                objectPosition: 'center',
              }}
              width="100%"
              height="100%"
            />
          ) : (
            isImage && <ImageL1Icon />
          )}
          {isVideo && showPreview && url ? (
            <Video
              controls={false}
              height="100%"
              sources={[
                {
                  src: url,
                  type: 'video/mp4',
                },
              ]}
            ></Video>
          ) : (
            isVideo && <VideoL1Icon />
          )}
          {isPdf && <PdfL1Icon />}
          {isAudio && <AudioL1Icon />}
          {(isDocument || isOther) && <FileL1Icon />}
        </>
      )}
    </Center>
  );
};

export default Asset;
